// react
import { useCallback, useMemo, useState } from 'react';

// mui
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

// components
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';

// validation
import { isValidEmail, isValidPhoneNumber } from '@karehero/models';

export interface ClientReferralStartData {
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientPhone: string;
  personReceivingCare: string;
  careRecipientFirstName: string;
  careRecipientLastName: string;
  context: string;
}

export interface ClientReferralStartPageProps {
  onBack?: () => void;
  onSubmit: (data: ClientReferralStartData) => void | Promise<void>;
  initialValues?: Partial<ClientReferralStartData>;
  isSubmitting?: boolean;
}

type FieldName =
  | 'clientFirstName'
  | 'clientLastName'
  | 'clientEmail'
  | 'clientPhone'
  | 'personReceivingCare'
  | 'careRecipientFirstName'
  | 'careRecipientLastName'
  | 'context';

export const ClientReferralStartPage = ({
  onBack,
  onSubmit,
  initialValues,
  isSubmitting = false,
}: ClientReferralStartPageProps) => {
  const [clientFirstName, setClientFirstName] = useState(
    initialValues?.clientFirstName || '',
  );
  const [clientLastName, setClientLastName] = useState(
    initialValues?.clientLastName || '',
  );
  const [clientEmail, setClientEmail] = useState(
    initialValues?.clientEmail || '',
  );
  const [clientPhone, setClientPhone] = useState(
    initialValues?.clientPhone || '',
  );
  const [personReceivingCare, setPersonReceivingCare] = useState(
    initialValues?.personReceivingCare || '',
  );
  const [careRecipientFirstName, setCareRecipientFirstName] = useState(
    initialValues?.careRecipientFirstName || '',
  );
  const [careRecipientLastName, setCareRecipientLastName] = useState(
    initialValues?.careRecipientLastName || '',
  );
  const [context, setContext] = useState(initialValues?.context || '');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});

  const validateField = useCallback(
    (field: FieldName, value: string): string => {
      switch (field) {
        case 'clientFirstName':
          return value.trim() ? '' : 'First name is required.';
        case 'clientLastName':
          return value.trim() ? '' : 'Last name is required.';
        case 'clientEmail':
          if (!value.trim()) return 'Email is required.';
          if (!isValidEmail(value.trim()))
            return 'Please enter a valid email address.';
          return '';
        case 'clientPhone':
          if (!value.trim()) return 'Phone number is required.';
          if (!isValidPhoneNumber(value.trim()))
            return 'Please enter a valid phone number.';
          return '';
        case 'personReceivingCare':
          return value ? '' : 'Please select who is receiving care.';
        case 'careRecipientFirstName':
          return value.trim() ? '' : 'First name is required.';
        case 'careRecipientLastName':
          return value.trim() ? '' : 'Last name is required.';
        case 'context':
          return value.trim() ? '' : 'Context is required.';
        default:
          return '';
      }
    },
    [],
  );

  const handleBlur = useCallback(
    (field: FieldName, value: string) => () => {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error || undefined }));
    },
    [validateField],
  );

  const handleFieldChange = useCallback(
    (field: FieldName, setter: (v: string) => void) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        setter(value);
        if (errors[field]) {
          setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
      },
    [errors],
  );

  const isValid = useMemo(() => {
    const isCareRecipientValid =
      personReceivingCare !== 'someone_else' ||
      (careRecipientFirstName.trim().length > 0 &&
        careRecipientLastName.trim().length > 0);

    return (
      clientFirstName.trim().length > 0 &&
      clientLastName.trim().length > 0 &&
      isValidEmail(clientEmail.trim()) &&
      !!isValidPhoneNumber(clientPhone.trim()) &&
      personReceivingCare.length > 0 &&
      isCareRecipientValid &&
      context.trim().length > 0
    );
  }, [
    clientFirstName,
    clientLastName,
    clientEmail,
    clientPhone,
    personReceivingCare,
    careRecipientFirstName,
    careRecipientLastName,
    context,
  ]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const fields: [FieldName, string][] = [
        ['clientFirstName', clientFirstName],
        ['clientLastName', clientLastName],
        ['clientEmail', clientEmail],
        ['clientPhone', clientPhone],
        ['personReceivingCare', personReceivingCare],
        ['context', context],
      ];
      if (personReceivingCare === 'someone_else') {
        fields.push(
          ['careRecipientFirstName', careRecipientFirstName],
          ['careRecipientLastName', careRecipientLastName],
        );
      }

      const newErrors: Partial<Record<FieldName, string>> = {};
      fields.forEach(([field, value]) => {
        const error = validateField(field, value);
        if (error) newErrors[field] = error;
      });
      setErrors(newErrors);

      if (Object.keys(newErrors).length > 0) return;

      await onSubmit({
        clientFirstName: clientFirstName.trim(),
        clientLastName: clientLastName.trim(),
        clientEmail: clientEmail.trim(),
        clientPhone: clientPhone.trim(),
        personReceivingCare,
        careRecipientFirstName: careRecipientFirstName.trim(),
        careRecipientLastName: careRecipientLastName.trim(),
        context: context.trim(),
      });
    },
    [
      clientFirstName,
      clientLastName,
      clientEmail,
      clientPhone,
      personReceivingCare,
      careRecipientFirstName,
      careRecipientLastName,
      context,
      validateField,
      onSubmit,
    ],
  );

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title='Client Referral - Client Information'
            description="We'll start by collecting some basic contact information about the referred client."
          />
        }
        contentSlot={
          <Box component='form' onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <TextField
                label="Client's First Name"
                value={clientFirstName}
                onChange={handleFieldChange(
                  'clientFirstName',
                  setClientFirstName,
                )}
                onBlur={handleBlur('clientFirstName', clientFirstName)}
                required
                fullWidth
                error={!!errors.clientFirstName}
                helperText={errors.clientFirstName}
                disabled={isSubmitting}
              />
              <TextField
                label="Client's Last Name"
                value={clientLastName}
                onChange={handleFieldChange(
                  'clientLastName',
                  setClientLastName,
                )}
                onBlur={handleBlur('clientLastName', clientLastName)}
                required
                fullWidth
                error={!!errors.clientLastName}
                helperText={errors.clientLastName}
                disabled={isSubmitting}
              />
              <TextField
                label="Client's Email"
                type='email'
                value={clientEmail}
                onChange={handleFieldChange('clientEmail', setClientEmail)}
                onBlur={handleBlur('clientEmail', clientEmail)}
                required
                fullWidth
                error={!!errors.clientEmail}
                helperText={
                  errors.clientEmail ||
                  "Please enter the client's personal email address."
                }
                disabled={isSubmitting}
              />
              <TextField
                label="Client's Phone Number"
                type='tel'
                value={clientPhone}
                onChange={handleFieldChange('clientPhone', setClientPhone)}
                onBlur={handleBlur('clientPhone', clientPhone)}
                required
                fullWidth
                error={!!errors.clientPhone}
                helperText={errors.clientPhone}
                disabled={isSubmitting}
              />

              <FormControl
                fullWidth
                disabled={isSubmitting}
                required
                error={!!errors.personReceivingCare}
              >
                <InputLabel id='person-receiving-care-label'>
                  Person Receiving Care
                </InputLabel>
                <Select
                  labelId='person-receiving-care-label'
                  value={personReceivingCare}
                  label='Person Receiving Care'
                  onChange={(e) => {
                    setPersonReceivingCare(e.target.value);
                    if (errors.personReceivingCare) {
                      setErrors((prev) => ({
                        ...prev,
                        personReceivingCare: undefined,
                      }));
                    }
                  }}
                >
                  <MenuItem value='the_client'>The Client</MenuItem>
                  <MenuItem value='someone_else'>Someone Else</MenuItem>
                </Select>
                {errors.personReceivingCare && (
                  <FormHelperText>{errors.personReceivingCare}</FormHelperText>
                )}
              </FormControl>

              {personReceivingCare === 'someone_else' && (
                <>
                  <TextField
                    label="Care Recipient's First Name"
                    value={careRecipientFirstName}
                    onChange={handleFieldChange(
                      'careRecipientFirstName',
                      setCareRecipientFirstName,
                    )}
                    onBlur={handleBlur(
                      'careRecipientFirstName',
                      careRecipientFirstName,
                    )}
                    required
                    fullWidth
                    error={!!errors.careRecipientFirstName}
                    helperText={errors.careRecipientFirstName}
                    disabled={isSubmitting}
                  />
                  <TextField
                    label="Care Recipient's Last Name"
                    value={careRecipientLastName}
                    onChange={handleFieldChange(
                      'careRecipientLastName',
                      setCareRecipientLastName,
                    )}
                    onBlur={handleBlur(
                      'careRecipientLastName',
                      careRecipientLastName,
                    )}
                    required
                    fullWidth
                    error={!!errors.careRecipientLastName}
                    helperText={errors.careRecipientLastName}
                    disabled={isSubmitting}
                  />
                </>
              )}

              <TextField
                label='Context about the situation'
                value={context}
                onChange={handleFieldChange('context', setContext)}
                onBlur={handleBlur('context', context)}
                required
                fullWidth
                multiline
                minRows={3}
                error={!!errors.context}
                helperText={errors.context}
                disabled={isSubmitting}
              />

              <Button
                type='submit'
                variant='contained'
                fullWidth
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? 'Submitting…' : 'Next'}
              </Button>
            </Stack>
          </Box>
        }
      />
    </ModalPageTemplate>
  );
};

ClientReferralStartPage.displayName = 'ClientReferralStartPage';
export default ClientReferralStartPage;
